import React, { useMemo } from 'react';
import { DisplayInputComponentProps } from '../../types';
import { FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Hook, Network, Toast } from 'jamespot-front-business';
import jApi, { WidgetAudience } from 'jamespot-user-api';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { AutocompleteView } from 'jamespot-react-components';

export const InputAudience = <TFieldValues extends FieldValues>(props: DisplayInputComponentProps<TFieldValues>) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const networkPublic = useAppSelector(Network.selectors.selectNetworkPublic);
    const JRCInputAudience = commonComponentsRegistry.getLazyComponent('InputAudience');

    const fileBankHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'FileBankHook'));
    const spotHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'SpotHook'));
    const businessContactHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'BusinessContactHook'));

    const views = useMemo<AutocompleteView[]>(() => {
        const customViews = (props.widget as WidgetAudience).options?.views;
        if (customViews) {
            return customViews;
        }

        const res: Array<AutocompleteView> = [];

        if (spotHook && spotHook.isActive && spotHook.hasLicense) {
            res.push('group');
        }

        res.push('user');

        if (fileBankHook && fileBankHook.isActive) {
            res.push('folder');
        }

        if (businessContactHook && businessContactHook.isActive) {
            res.push('businessContact');
        }

        return res;
    }, [businessContactHook, fileBankHook, props.widget, spotHook]);

    return (
        <JRCInputAudience
            dataCy="audience-input"
            label={intl.formatMessage({ id: 'GLOBAL_Audience' })}
            name={props.name}
            control={props.control}
            multiple
            searchable
            placeholder={intl.formatMessage({ id: 'GLOBAL_Audience' })}
            defaultCustomOptions={
                networkPublic && !(props.widget as WidgetAudience).options?.disableEveryoneAudience
                    ? [
                          {
                              uri: networkPublic.uri,
                              title: networkPublic.title,
                              resetOptions: true,
                              icon: 'icon-fs-wall',
                          },
                      ]
                    : []
            }
            views={{
                activeViews: views,
                handlers: {
                    user: jApi.user,
                    filebank: jApi.filebank,
                    group: jApi.group,
                },
                onError: (message?: string) => {
                    dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
                },
            }}
        />
    );
};
